class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit);
      facetForm.addEventListener('change', this.debouncedOnSubmit);
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    this.filterSortBarToggleHandler = this.onFilterSortBarToggle.bind(this);
    this.addEventListener('toggle', this.filterSortBarToggleHandler, true);
    this.bindFilterSortBarSummaryActivation();
  }

  bindFilterSortBarSummaryActivation() {
    const filterSortBar = this.closest('.page-product-listing__filter-sort-bar');
    if (!filterSortBar) return;

    filterSortBar.querySelectorAll('.facets__scroll .facets__disclosure .facets__summary').forEach((summary) => {
      if (summary.dataset.filterSortBarBound === 'true') return;
      summary.dataset.filterSortBarBound = 'true';

      // summary を確実にフォーカス可能に
      if (!summary.hasAttribute('tabindex')) summary.setAttribute('tabindex', '0');

      summary.addEventListener('pointerdown', () => {
        const details = summary.closest('details.facets__disclosure');
        if (details) summary.dataset.filterSortWasOpen = details.open ? '1' : '0';
      });

      summary.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const details = summary.closest('details.facets__disclosure');
        if (!details) return;
        event.preventDefault();
        event.stopPropagation();
        const willOpen = !details.open;
        details.dataset.openedByKeyboard = willOpen ? '1' : '0';
        details.open = willOpen;
      });

      summary.addEventListener('click', () => {
        if (!FacetFiltersForm.isFilterSortBarMobile()) return;
        const details = summary.closest('details.facets__disclosure');
        if (!details) return;

        requestAnimationFrame(() => {
          if (summary.dataset.filterSortWasOpen === '0' && !details.open) {
            filterSortBar.querySelectorAll('.facets__disclosure[open]').forEach((openDetails) => {
              if (openDetails !== details) {
                FacetFiltersForm.resetFilterSortBarPanelPosition(openDetails);
                openDetails.removeAttribute('open');
              }
            });
            details.setAttribute('open', '');
          }
          if (details.open) {
            FacetFiltersForm.positionFilterSortBarPanel(details);
          } else {
            FacetFiltersForm.resetFilterSortBarPanelPosition(details);
          }
          delete summary.dataset.filterSortWasOpen;
        });
      });
    });
  }

  disconnectedCallback() {
    if (this.filterSortBarToggleHandler) {
      this.removeEventListener('toggle', this.filterSortBarToggleHandler, true);
    }
  }

  onFilterSortBarToggle(event) {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.classList.contains('facets__disclosure')) return;

    const filterSortBar = details.closest('.page-product-listing__filter-sort-bar');
    if (!filterSortBar) return;

    if (!details.open) {
      FacetFiltersForm.resetFilterSortBarPanelPosition(details);
      return;
    }

    filterSortBar.querySelectorAll('.facets__disclosure[open]').forEach((openDetails) => {
      if (openDetails !== details) {
        FacetFiltersForm.resetFilterSortBarPanelPosition(openDetails);
        openDetails.removeAttribute('open');
      }
    });

    FacetFiltersForm.positionFilterSortBarPanel(details);

    // キーボードで開いたときは、開いたキーの keyup 後にチェックへフォーカス（Space 誤トグル防止）
    if (details.dataset.openedByKeyboard === '1') {
      delete details.dataset.openedByKeyboard;
      FacetFiltersForm.scheduleFocusFirstFilterControl(details);
    }
  }

  static isFilterSortBarMobile() {
    return window.matchMedia('(max-width: 1079px)').matches;
  }

  static getFilterSortBarDisplay(details) {
    const entry = FacetFiltersForm.filterSortBarPortal?.get(details);
    return entry?.display || details.querySelector('.facets__display');
  }

  static linkFilterSortBarPortalFields(display, details) {
    const facetFormComponent = details.closest('facet-filters-form');
    const form = facetFormComponent?.querySelector('form');
    if (!form?.id || !display) return;

    display.querySelectorAll('input, select, textarea').forEach((field) => {
      field.setAttribute('form', form.id);
    });
  }

  static focusFirstFilterControl(details) {
    const display =
      FacetFiltersForm.getFilterSortBarDisplay(details) || details.querySelector('.facets__display');
    if (!display) return;
    const focusTarget = display.querySelector(
      'input[type="checkbox"]:not([disabled]), input[type="radio"]:not([disabled]), input:not([type="hidden"]):not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    );
    if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus({ preventScroll: true });
    }
  }

  static scheduleFocusFirstFilterControl(details) {
    const focusWhenIdle = () => FacetFiltersForm.focusFirstFilterControl(details);
    const onKeyUp = (event) => {
      if (event.key !== ' ' && event.key !== 'Enter') return;
      window.removeEventListener('keyup', onKeyUp, true);
      clearTimeout(fallbackTimer);
      requestAnimationFrame(focusWhenIdle);
    };
    window.addEventListener('keyup', onKeyUp, true);
    const fallbackTimer = setTimeout(() => {
      window.removeEventListener('keyup', onKeyUp, true);
      focusWhenIdle();
    }, 300);
  }

  static portalFilterSortBarPanel(details) {
    if (!FacetFiltersForm.isFilterSortBarMobile()) return;

    const display = details.querySelector('.facets__display');
    if (!display || display.dataset.portaled === 'true') return;

    const placeholder = document.createComment('facet-sort-bar-display');
    display.parentNode.insertBefore(placeholder, display);
    document.body.appendChild(display);
    display.dataset.portaled = 'true';
    display.classList.add('facets__display--filter-sort-portal');
    FacetFiltersForm.linkFilterSortBarPortalFields(display, details);
    if (!FacetFiltersForm.filterSortBarPortal) FacetFiltersForm.filterSortBarPortal = new Map();
    FacetFiltersForm.filterSortBarPortal.set(details, { placeholder, display });
  }

  static unportalFilterSortBarPanel(details) {
    if (!FacetFiltersForm.filterSortBarPortal) return;
    const entry = FacetFiltersForm.filterSortBarPortal.get(details);
    if (!entry) return;

    const { placeholder, display } = entry;
    display.removeAttribute('style');
    display.classList.remove('facets__display--filter-sort-portal');
    delete display.dataset.portaled;

    if (placeholder.parentNode) {
      placeholder.parentNode.insertBefore(display, placeholder);
      placeholder.remove();
    } else {
      display.remove();
    }

    FacetFiltersForm.filterSortBarPortal.delete(details);
  }

  static clearFilterSortBarPortals() {
    if (FacetFiltersForm.filterSortBarPortal) {
      Array.from(FacetFiltersForm.filterSortBarPortal.keys()).forEach((details) => {
        FacetFiltersForm.unportalFilterSortBarPanel(details);
      });
    }
    document.querySelectorAll('body > .facets__display--filter-sort-portal').forEach((node) => node.remove());
  }

  static resetFilterSortBarPanelPosition(details) {
    const display = FacetFiltersForm.getFilterSortBarDisplay(details);
    if (display) display.removeAttribute('style');
    FacetFiltersForm.unportalFilterSortBarPanel(details);
  }

  static armFilterSortBarScrollGuard() {
    FacetFiltersForm.filterSortBarIgnoreScrollUntil = performance.now() + 500;
    FacetFiltersForm.filterSortBarLastScrollY = window.scrollY;
  }

  static shouldIgnoreFilterSortBarScrollClose() {
    return performance.now() < (FacetFiltersForm.filterSortBarIgnoreScrollUntil || 0);
  }

  static positionFilterSortBarPanel(details) {
    const summary = details.querySelector('.facets__summary');
    if (!summary || !FacetFiltersForm.isFilterSortBarMobile()) return;

    FacetFiltersForm.portalFilterSortBarPanel(details);
    const display = FacetFiltersForm.getFilterSortBarDisplay(details);
    if (!display) return;

    FacetFiltersForm.armFilterSortBarScrollGuard();

    const place = () => {
      const rect = summary.getBoundingClientRect();
      const gap = 8;
      const edge = 12;
      const left = Math.max(edge, Math.min(rect.left, window.innerWidth - edge - 200));
      const width = Math.min(350, window.innerWidth - left - edge);
      const maxHeight = Math.max(120, window.innerHeight - rect.bottom - gap - edge);

      display.style.position = 'fixed';
      display.style.top = `${rect.bottom + gap}px`;
      display.style.left = `${left}px`;
      display.style.right = 'auto';
      display.style.transform = 'none';
      display.style.width = `${width}px`;
      display.style.maxHeight = `${maxHeight}px`;
      display.style.zIndex = '1000';
    };

    place();
    requestAnimationFrame(() => {
      place();
      requestAnimationFrame(place);
    });
  }

  static closeFilterSortBarOpenPanels() {
    document.querySelectorAll('.page-product-listing__filter-sort-bar .facets__disclosure[open]').forEach((details) => {
      details.removeAttribute('open');
      FacetFiltersForm.resetFilterSortBarPanelPosition(details);
    });
    FacetFiltersForm.clearFilterSortBarPortals();
  }

  static isFilterSortBarOutsideClickTarget(target) {
    if (!(target instanceof Element)) return true;
    // SP: body 直下へ移したパネル内
    if (target.closest('.facets__display--filter-sort-portal')) return false;
    // PC/SP: 開いている disclosure（summary + パネル）内
    if (target.closest('.page-product-listing__filter-sort-bar .facets__disclosure[open]')) return false;
    return true;
  }

  /** 在庫チェック等を選んだらパネルを閉じる対象か */
  static shouldCloseFilterSortBarOnFacetEvent(event) {
    const target = event?.target;
    if (!(target instanceof Element)) return false;
    if (
      !target.closest(
        '.page-product-listing__filter-sort-bar .facets__display, .facets__display--filter-sort-portal'
      )
    ) {
      return false;
    }
    if (target instanceof HTMLInputElement) {
      return target.type === 'checkbox' || target.type === 'radio';
    }
    return target instanceof HTMLSelectElement;
  }

  /**
   * body 直下へ移したパネル内の change を facet-filters-form へ中継する。
   * unportal 前に呼ぶこと（ポータル class が消えると中継できない）。
   */
  static relayFilterSortBarPortalChange(event) {
    const field = event?.target;
    if (!field || typeof field.closest !== 'function') return false;
    if (!field.closest('.facets__display--filter-sort-portal')) return false;

    const formId = field.getAttribute('form');
    const form = formId ? document.getElementById(formId) : null;
    const facetFormComponent = form?.closest('facet-filters-form');
    if (!facetFormComponent || typeof facetFormComponent.debouncedOnSubmit !== 'function') return false;

    facetFormComponent.debouncedOnSubmit(event);
    return true;
  }

  static initFilterSortBarOutsideClose() {
    if (FacetFiltersForm.filterSortBarOutsideCloseInit) return;
    FacetFiltersForm.filterSortBarOutsideCloseInit = true;

    // pointerdown: 開く操作と同じクリックで即閉じない（open 前は対象なし）
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!document.querySelector('.page-product-listing__filter-sort-bar .facets__disclosure[open]')) return;
        if (!FacetFiltersForm.isFilterSortBarOutsideClickTarget(event.target)) return;
        FacetFiltersForm.closeFilterSortBarOpenPanels();
      },
      true
    );

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!document.querySelector('.page-product-listing__filter-sort-bar .facets__disclosure[open]')) return;
      FacetFiltersForm.closeFilterSortBarOpenPanels();
    });

    // チェック等を選んだら即閉じる（送信の debounce 待ちにしない）
    // SP ポータル中は閉じる前に送信を中継する（先に unportal すると中継できない）
    document.addEventListener(
      'change',
      (event) => {
        if (!FacetFiltersForm.shouldCloseFilterSortBarOnFacetEvent(event)) return;
        FacetFiltersForm.relayFilterSortBarPortalChange(event);
        FacetFiltersForm.closeFilterSortBarOpenPanels();
      },
      true
    );
  }

  static initFilterSortBarPortalListeners() {
    if (FacetFiltersForm.filterSortBarPortalListenersInit) return;
    FacetFiltersForm.filterSortBarPortalListenersInit = true;

    window.addEventListener('resize', () => {
      if (!FacetFiltersForm.isFilterSortBarMobile()) return;
      document.querySelectorAll('.page-product-listing__filter-sort-bar .facets__disclosure[open]').forEach((details) => {
        FacetFiltersForm.positionFilterSortBarPanel(details);
      });
    });

    /** body 直下のパネルは form の外にあるため change を facet-filters-form へ中継（閉じない操作向けの保険） */
    document.addEventListener(
      'change',
      (event) => {
        FacetFiltersForm.relayFilterSortBarPortalChange(event);
      },
      true
    );

    /**
     * カスタム見た目のチェックは Space/Enter が効かない環境があるため、
     * 絞り込みバー内は明示的にトグルする。
     */
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (input.type !== 'checkbox' || input.disabled) return;
      if (
        !input.closest(
          '.page-product-listing__filter-sort-bar .facets__display, .facets__display--filter-sort-portal'
        )
      ) {
        return;
      }

      event.preventDefault();
      input.checked = !input.checked;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  static resolveFacetSubmitForm(eventTarget) {
    if (!eventTarget) return null;
    const fromClosest = eventTarget.closest?.('form');
    if (fromClosest) return fromClosest;

    const associated = eventTarget.form;
    if (associated instanceof HTMLFormElement) return associated;

    const formId = eventTarget.getAttribute?.('form');
    if (formId) {
      const byId = document.getElementById(formId);
      if (byId instanceof HTMLFormElement) return byId;
    }

    return null;
  }

  static initFilterSortBarScrollClose() {
    if (FacetFiltersForm.filterSortBarScrollCloseInit) return;
    FacetFiltersForm.filterSortBarScrollCloseInit = true;
    FacetFiltersForm.filterSortBarLastScrollY = window.scrollY;

    let scrollCloseScheduled = false;
    const onPageScroll = () => {
      if (!FacetFiltersForm.isFilterSortBarMobile()) return;
      if (!document.querySelector('.page-product-listing__filter-sort-bar .facets__disclosure[open]')) return;
      if (FacetFiltersForm.shouldIgnoreFilterSortBarScrollClose()) return;

      const scrollY = window.scrollY;
      const delta = Math.abs(scrollY - (FacetFiltersForm.filterSortBarLastScrollY || 0));
      FacetFiltersForm.filterSortBarLastScrollY = scrollY;
      if (delta < 10) return;

      if (scrollCloseScheduled) return;
      scrollCloseScheduled = true;
      requestAnimationFrame(() => {
        scrollCloseScheduled = false;
        FacetFiltersForm.closeFilterSortBarOpenPanels();
      });
    };

    window.addEventListener('scroll', onPageScroll, { passive: true });
  }

  static findFacetScope(el) {
    if (!el || typeof el.closest !== 'function') return null;
    return el.closest('[data-product-grid-container-id]');
  }

  static resolveActiveScope(event) {
    const fromTarget = event?.target && FacetFiltersForm.findFacetScope(event.target);
    if (fromTarget) return fromTarget;
    const fromCurrent = event?.currentTarget && FacetFiltersForm.findFacetScope(event.currentTarget);
    if (fromCurrent) return fromCurrent;
    return document.querySelector('[data-product-grid-container-id]') || null;
  }

  static getResolvedFormId(baseId) {
    const suffix = FacetFiltersForm.activeScope?.dataset?.facetFormSuffix || '';
    return suffix ? `${baseId}--${suffix}` : baseId;
  }

  static collectJsFiltersFromRoot(root) {
    const bases = ['FacetFiltersForm', 'FacetFiltersFormMobile', 'FacetFiltersPillsForm'];
    const suffix = FacetFiltersForm.activeScope?.dataset?.facetFormSuffix || '';
    const out = [];
    bases.forEach((base) => {
      const id = suffix ? `${base}--${suffix}` : base;
      const form = root.getElementById(id);
      if (form) {
        form.querySelectorAll('.js-filter').forEach((el) => out.push(el));
      }
    });
    return out;
  }

  static collectJsFiltersFromDocument() {
    if (!FacetFiltersForm.activeScope?.dataset?.facetFormSuffix) {
      return Array.from(
        document.querySelectorAll(
          '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
        )
      );
    }
    return FacetFiltersForm.collectJsFiltersFromRoot(document);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  /** Locale-aware store path (e.g. /ja/collections/handle) */
  static storePath(relativePath) {
    const root =
      typeof window.Shopify !== 'undefined' && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    const path = relativePath.startsWith('/') ? relativePath.slice(1) : relativePath;
    const base = root.endsWith('/') ? root : `${root}/`;
    return `${base}${path}`;
  }

  /** Same logical query regardless of param order (avoids reload loops vs window.location.search). */
  static normalizeQueryString(qs) {
    if (qs == null || qs === '') return '';
    let params;
    try {
      params = new URLSearchParams(String(qs));
    } catch {
      return String(qs);
    }
    const pairs = Array.from(params.entries());
    pairs.sort((a, b) => {
      const keyCmp = a[0].localeCompare(b[0]);
      return keyCmp !== 0 ? keyCmp : String(a[1]).localeCompare(String(b[1]));
    });
    const out = new URLSearchParams();
    pairs.forEach(([k, v]) => out.append(k, v));
    return out.toString();
  }

  static queriesSearchParamsEqual(a, b) {
    return FacetFiltersForm.normalizeQueryString(a) === FacetFiltersForm.normalizeQueryString(b);
  }

  static afterSectionDomUpdate(productGridContainerId) {
    if (typeof initializeScrollAnimationTrigger !== 'function') return;
    let root = document;
    if (productGridContainerId) {
      const el = document.getElementById(productGridContainerId);
      if (el) root = el;
    }
    initializeScrollAnimationTrigger(root);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.activeScope = FacetFiltersForm.resolveActiveScope(event);
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    const containerId = sections[0]?.containerId || 'ProductGridContainer';
    const gridHost = document.getElementById(containerId);
    const collectionEl = gridHost?.querySelector('.collection');
    if (collectionEl) {
      collectionEl.classList.add('loading');
    }
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    const scope = FacetFiltersForm.activeScope;
    const listingHandle = scope?.dataset?.listingCollectionHandle;
    const listingRemoteFetchBase = scope?.dataset?.listingRemoteFetchBase?.trim();
    const remoteGridSectionId = scope?.dataset?.listingGridRemoteSectionId || 'product-grid';
    const pageSectionId = sections[0]?.section;
    const cid = sections[0]?.containerId;

    const runSingleFetch = () => {
      sections.forEach(({ section, containerId: cidInner }) => {
        const url = `${window.location.pathname}?section_id=${encodeURIComponent(section)}&${searchParams}`;
        const filterDataUrl = (element) => element.url === url;

        FacetFiltersForm.filterData.some(filterDataUrl)
          ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, cidInner)
          : FacetFiltersForm.renderSectionFromFetch(url, event, cidInner);
      });
    };

    if (scope?.dataset?.skipListingAjaxSync === 'true') {
      const cur = window.location.search.slice(1);
      if (!FacetFiltersForm.queriesSearchParamsEqual(searchParams, cur)) {
        window.location.assign(
          searchParams ? `${window.location.pathname}?${searchParams}` : window.location.pathname
        );
      }
      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
      loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
      if (collectionEl) collectionEl.classList.remove('loading');
      if (countContainer) countContainer.classList.remove('loading');
      if (countContainerDesktop) countContainerDesktop.classList.remove('loading');
      return;
    }

    /** 固定ページの商品一覧: /collections へは取りに行かず、同ページの section のみ取得（404・ハンドル不一致を避ける） */
    if (scope?.dataset?.pageProductListingFetchMode === 'section-only') {
      runSingleFetch();
      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
      return;
    }

    if (listingHandle && pageSectionId && cid) {
      const facetUrl = `${window.location.pathname}?section_id=${encodeURIComponent(pageSectionId)}&${searchParams}`;
      const encodedHandle = encodeURIComponent(listingHandle);
      const gridPath = listingRemoteFetchBase
        ? listingRemoteFetchBase.split('?')[0]
        : FacetFiltersForm.storePath(`collections/${encodedHandle}`);
      const gridUrl = `${gridPath}?section_id=${encodeURIComponent(remoteGridSectionId)}&${searchParams}`;

      const fetchText = (url) =>
        fetch(url, { credentials: 'same-origin' }).then((response) => {
          if (!response.ok) throw new Error(response.statusText);
          return response.text();
        });

      Promise.all([fetchText(facetUrl), fetchText(gridUrl)])
        .then(([facetHtml, gridHtml]) => {
          FacetFiltersForm.filterData = [
            ...FacetFiltersForm.filterData,
            { html: facetHtml, url: facetUrl },
            { html: gridHtml, url: gridUrl },
          ];
          FacetFiltersForm.renderFilters(facetHtml, event);
          FacetFiltersForm.renderProductGridContainer(gridHtml, cid, { replaceProductListOnly: true });
          FacetFiltersForm.renderProductCount(facetHtml);
          FacetFiltersForm.afterSectionDomUpdate(cid);
        })
        .catch(() => {
          const curQs = window.location.search.slice(1);
          if (FacetFiltersForm.queriesSearchParamsEqual(searchParams, curQs)) {
            runSingleFetch();
          } else {
            window.location.assign(
              searchParams ? `${window.location.pathname}?${searchParams}` : window.location.pathname
            );
          }
        });
    } else {
      runSingleFetch();
    }

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, containerId) {
    fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(response.statusText || String(response.status));
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html, containerId);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.afterSectionDomUpdate(containerId);
      })
      .catch(() => {
        document
          .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
          .forEach((spinner) => spinner.classList.add('hidden'));
        const host = document.getElementById(containerId);
        host?.querySelector('.collection')?.classList.remove('loading');
        document.getElementById('ProductCount')?.classList.remove('loading');
        document.getElementById('ProductCountDesktop')?.classList.remove('loading');
      });
  }

  static renderSectionFromCache(filterDataUrl, event, containerId) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html, containerId);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.afterSectionDomUpdate(containerId);
  }

  static renderProductGridContainer(html, containerId = 'ProductGridContainer', options = {}) {
    const { replaceProductListOnly = false } = options;
    const target = document.getElementById(containerId);
    if (!target) return;
    const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
    const parsed =
      parsedDoc.getElementById(containerId) ||
      parsedDoc.getElementById('ProductGridContainer') ||
      parsedDoc.querySelector('#ProductGridContainer');
    if (!parsed) return;

    if (replaceProductListOnly) {
      const sourceUl =
        parsed.querySelector('ul#product-grid') || parsed.querySelector('ul.grid.product-grid');
      const targetUl =
        target.querySelector('ul[id^="product-grid"]') || target.querySelector('ul.grid.product-grid');
      if (sourceUl && targetUl) {
        targetUl.innerHTML = sourceUl.innerHTML;
      } else {
        target.innerHTML = parsed.innerHTML;
      }
    } else {
      target.innerHTML = parsed.innerHTML;
    }

    target.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
    FacetFiltersForm.patchPageListingPaginationLinks();
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const fetchedCount = parsedHTML.getElementById('ProductCount');
    const count = fetchedCount ? fetchedCount.innerHTML : '';
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = FacetFiltersForm.collectJsFiltersFromRoot(parsedHTML);
    const facetDetailsElementsFromDom = FacetFiltersForm.collectJsFiltersFromDocument();

    // Remove facets that are no longer returned from the server
    facetDetailsElementsFromDom.forEach((currentElement) => {
      if (!facetDetailsElementsFromFetch.some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = facetDetailsElementsFromFetch.filter((element) => !matchesId(element));
    const countsToRender = facetDetailsElementsFromFetch.find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const parent = document.getElementById(elementToRender.parentElement.id);
          const anchor = parent?.querySelector('.js-filter');
          if (parent && anchor) {
            anchor.before(elementToRender);
          }
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const targets = document.querySelectorAll(selector);
      if (!targets.length) return;

      // 新HTMLに無い / 空のときは既存ピルを消す（0件でコンテナごと消えると古いピルが残る）
      const nextHtml = activeFacetsElement ? activeFacetsElement.innerHTML : '';
      targets.forEach((target) => {
        target.innerHTML = nextHtml;
        const wrap = target.closest('.page-product-listing__filter-sort-bar__active');
        if (wrap) {
          const empty = !nextHtml.trim();
          wrap.hidden = empty;
          wrap.classList.toggle('page-product-listing__filter-sort-bar__active--empty', empty);
        }
      });
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    FacetFiltersForm.clearFilterSortBarPortals();

    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const sourceEl = html.querySelector(selector);
      if (!sourceEl) return;
      const targetEl = document.querySelector(selector);
      if (!targetEl) return;
      targetEl.innerHTML = sourceEl.innerHTML;
    });

    const sourceHeadings = html.querySelectorAll('.mobile-facets__inner .mobile-facets__heading');
    const targetHeadings = document.querySelectorAll('.mobile-facets__inner .mobile-facets__heading');
    sourceHeadings.forEach((src, i) => {
      if (targetHeadings[i]) targetHeadings[i].innerHTML = src.innerHTML;
    });

    const mobileForm = document.getElementById(FacetFiltersForm.getResolvedFormId('FacetFiltersFormMobile'));
    if (mobileForm?.closest('menu-drawer')?.bindEvents) {
      mobileForm.closest('menu-drawer').bindEvents();
    }

    FacetFiltersForm.syncMobileFacetSortText();
    FacetFiltersForm.patchPageListingChipLinks();
    FacetFiltersForm.patchPageListingPaginationLinks();
    FacetFiltersForm.rebindFilterSortBarSummaries();
  }

  static rebindFilterSortBarSummaries() {
    document.querySelectorAll('.page-product-listing__filter-sort-bar facet-filters-form').forEach((form) => {
      if (typeof form.bindFilterSortBarSummaryActivation === 'function') {
        form.bindFilterSortBarSummaryActivation();
      }
    });
  }

  /** ページ商品一覧: URL に sort_by / filter があっても Liquid の並びが変わらないため、コレクション文脈でグリッドを同期 */
  static listingGridNeedsCollectionSync(params) {
    if (params.get('sort_by')) return true;
    for (const key of params.keys()) {
      if (key.startsWith('filter.')) return true;
    }
    return false;
  }

  /**
   * PC/SP の表示件数（viewport 基準）。リンク引き継ぎで誤件数を焼かない。
   */
  static resolveListingPerPage(scope) {
    const root =
      (scope && scope.closest && scope.closest('[data-listing-per-mobile]')) ||
      document.querySelector('[data-listing-per-mobile]');
    const mobile = parseInt((root && root.getAttribute('data-listing-per-mobile')) || '8', 10) || 8;
    const desktop = parseInt((root && root.getAttribute('data-listing-per-desktop')) || '16', 10) || 16;
    return window.matchMedia('(max-width: 1079px)').matches ? mobile : desktop;
  }

  static syncPageListingGridIfNeeded() {
    const scope = document.querySelector('[data-listing-collection-handle][data-listing-grid-remote-section-id]');
    if (!scope || !scope.dataset.listingCollectionHandle) return;
    if (scope.dataset.skipListingAjaxSync === 'true') return;
    const qs = window.location.search.slice(1);
    if (!qs) return;
    const params = new URLSearchParams(window.location.search);
    if (!FacetFiltersForm.listingGridNeedsCollectionSync(params)) return;
    FacetFiltersForm.renderPage(qs, { target: scope }, false);
  }

  /** チップ遷移先 URL に、現在の並び替え・絞り込み・件数を引き継ぐ（category/collection/scene はチップ側を優先） */
  static mergeListingSearchStateIntoUrl(targetUrl, sourceParams, sortVal, { preservePage = false } = {}) {
    if (sortVal) targetUrl.searchParams.set('sort_by', sortVal);
    targetUrl.searchParams.set('per_page', String(FacetFiltersForm.resolveListingPerPage()));
    if (sourceParams.has('q')) {
      targetUrl.searchParams.set('q', sourceParams.get('q'));
    }
    if (sourceParams.has('options[prefix]')) {
      targetUrl.searchParams.set('options[prefix]', sourceParams.get('options[prefix]'));
    }
    ['category', 'collection', 'scene', 'childCategory'].forEach((key) => {
      if (sourceParams.has(key) && !targetUrl.searchParams.has(key)) {
        targetUrl.searchParams.set(key, sourceParams.get(key));
      }
    });
    const filterKeys = [];
    sourceParams.forEach((_, key) => {
      if (key.startsWith('filter.') && !filterKeys.includes(key)) filterKeys.push(key);
    });
    filterKeys.forEach((key) => {
      targetUrl.searchParams.delete(key);
      sourceParams.getAll(key).forEach((v) => targetUrl.searchParams.append(key, v));
    });
    targetUrl.searchParams.delete('section_id');
    if (!preservePage) {
      targetUrl.searchParams.delete('page');
    }
  }

  static mergeListingSearchIntoChipUrl(targetUrl, sourceParams, sortVal) {
    FacetFiltersForm.mergeListingSearchStateIntoUrl(targetUrl, sourceParams, sortVal, { preservePage: false });
  }

  /** 絞り込み解除 URL に現在 URL のタブ・件数・検索語を引き継ぐ（Shopify url_to_remove は collection 等を含まない） */
  static mergeListingTabParamsFromCurrentUrl(searchParams) {
    try {
      const current = new URLSearchParams(window.location.search);
      const merged = new URLSearchParams(searchParams || '');
      ['category', 'collection', 'scene', 'childCategory', 'per_page'].forEach((key) => {
        if (current.has(key) && !merged.has(key)) {
          merged.set(key, current.get(key));
        }
      });
      if (current.has('q') && !merged.has('q')) {
        merged.set('q', current.get('q'));
      }
      if (current.has('options[prefix]') && !merged.has('options[prefix]')) {
        merged.set('options[prefix]', current.get('options[prefix]'));
      }
      return merged.toString();
    } catch {
      return searchParams;
    }
  }

  static getPageListingSortForChips() {
    const KEY = FacetFiltersForm.PAGE_LISTING_SORT_KEY;
    try {
      let curSort = new URLSearchParams(window.location.search).get('sort_by');
      if (curSort) {
        try {
          sessionStorage.setItem(KEY, curSort);
        } catch {
          /* ignore */
        }
        return curSort;
      }
      try {
        curSort = sessionStorage.getItem(KEY);
        if (curSort) return curSort;
      } catch {
        /* ignore */
      }
      const sel = document.querySelector('select[name="sort_by"]');
      if (sel?.value) {
        try {
          sessionStorage.setItem(KEY, sel.value);
        } catch {
          /* ignore */
        }
        return sel.value;
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  static patchPageListingChipLinks() {
    const nav = document.querySelector('.page-product-listing__chip-list');
    if (!nav) return;
    try {
      const curSort = FacetFiltersForm.getPageListingSortForChips();
      const loc = new URL(window.location.href);
      nav.querySelectorAll('a.page-product-listing__chip').forEach((a) => {
        const base = a.getAttribute('href');
        if (!base) return;
        const u = new URL(base, window.location.href);
        FacetFiltersForm.mergeListingSearchIntoChipUrl(u, loc.searchParams, curSort);
        a.setAttribute('href', u.pathname + u.search + (u.hash || ''));
      });
    } catch {
      /* ignore */
    }
  }

  static patchPageListingPaginationLinks() {
    const paginationLinks = document.querySelectorAll(
      '.page-product-listing-section--search-category .pagination-wrapper a.pagination__item, .page-product-listing-section--search-scene .pagination-wrapper a.pagination__item, .page-product-listing-section--search-results .pagination-wrapper a.pagination__item, .page-product-listing-section--search-best-sellers .pagination-wrapper a.pagination__item'
    );
    if (!paginationLinks.length) return;
    try {
      const curSort = FacetFiltersForm.getPageListingSortForChips();
      const loc = new URL(window.location.href);
      paginationLinks.forEach((a) => {
        const base = a.getAttribute('href');
        if (!base) return;
        const u = new URL(base, window.location.href);
        if (loc.searchParams.has('q') && !u.searchParams.has('q')) {
          u.searchParams.set('q', loc.searchParams.get('q'));
        }
        if (loc.searchParams.has('options[prefix]')) {
          u.searchParams.set('options[prefix]', loc.searchParams.get('options[prefix]'));
        } else if (u.pathname.includes('/search') && !u.searchParams.has('options[prefix]')) {
          u.searchParams.set('options[prefix]', 'last');
        }
        FacetFiltersForm.mergeListingSearchStateIntoUrl(u, loc.searchParams, curSort, { preservePage: true });
        a.setAttribute('href', u.pathname + u.search + (u.hash || ''));
      });
    } catch {
      /* ignore */
    }
  }

  static patchPageListingNavigationLinks() {
    FacetFiltersForm.patchPageListingChipLinks();
    FacetFiltersForm.patchPageListingPaginationLinks();
  }

  static initPageListingLinkPatchListeners() {
    if (FacetFiltersForm.pageListingLinkPatchListenersInitialized) return;
    FacetFiltersForm.pageListingLinkPatchListenersInitialized = true;
    document.addEventListener(
      'pointerdown',
      (event) => {
        const target = event.target;
        if (!target || typeof target.closest !== 'function') return;
        if (target.closest('a.page-product-listing__chip')) {
          FacetFiltersForm.patchPageListingChipLinks();
          return;
        }
        if (
          target.closest(
            '.page-product-listing-section--search-category .pagination-wrapper a.pagination__item, .page-product-listing-section--search-scene .pagination-wrapper a.pagination__item, .page-product-listing-section--search-results .pagination-wrapper a.pagination__item'
          )
        ) {
          FacetFiltersForm.patchPageListingPaginationLinks();
        }
      },
      true
    );
  }

  /** @deprecated 互換用。patchPageListingChipLinks を呼ぶ */
  static patchPageListingChipSortLinks() {
    FacetFiltersForm.patchPageListingChipLinks();
  }

  static syncMobileFacetSortText() {
    const params = new URLSearchParams(window.location.search);
    let sortByParam = params.get('sort_by');
    if (sortByParam) {
      try {
        sortByParam = decodeURIComponent(sortByParam);
      } catch {
        /* keep raw */
      }
    }

    const normalizeSortKey = (v) => {
      if (v == null || v === '') return '';
      let s = String(v).trim();
      try {
        s = decodeURIComponent(s);
      } catch {
        /* keep */
      }
      return s
        .toLowerCase()
        .replace(/\+/g, ' ')
        .replace(/_/g, '-');
    };

    const sortKey = normalizeSortKey(sortByParam);
    const candidates = Array.from(document.querySelectorAll('select[name="sort_by"]'));
    if (!sortKey || !candidates.length) return;

    candidates.forEach((select) => {
      for (let i = 0; i < select.options.length; i++) {
        const o = select.options[i];
        if (normalizeSortKey(o.value) === sortKey) {
          select.value = o.value;
          break;
        }
      }
    });
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static getPageListingScope() {
    return document.querySelector('[data-listing-collection-handle][data-listing-grid-remote-section-id]');
  }

  static isSearchListingTemplate(scope) {
    if (!scope || typeof scope.closest !== 'function') return false;
    return Boolean(
      scope.closest('.page-product-listing-section--search-category, .page-product-listing-section--search-scene')
    );
  }

  static buildPersistentListingParams(paramsLike) {
    const src = paramsLike instanceof URLSearchParams ? paramsLike : new URLSearchParams(paramsLike || '');
    const out = new URLSearchParams();
    src.forEach((value, key) => {
      if (key === 'sort_by' || key === 'per_page' || key === 'category' || key === 'collection' || key === 'scene' || key === 'childCategory') {
        out.append(key, value);
        return;
      }
      if (key.startsWith('filter.')) {
        out.append(key, value);
      }
    });
    return out;
  }

  static getListingStateStorageKey(pathname = window.location.pathname) {
    return `${FacetFiltersForm.PAGE_LISTING_STATE_KEY_PREFIX}:${pathname}`;
  }

  static persistListingStateFromSearchParams(paramsLike) {
    const scope = FacetFiltersForm.getPageListingScope();
    if (!FacetFiltersForm.isSearchListingTemplate(scope)) return;
    const persisted = FacetFiltersForm.buildPersistentListingParams(paramsLike).toString();
    const key = FacetFiltersForm.getListingStateStorageKey();
    try {
      if (persisted) {
        sessionStorage.setItem(key, persisted);
      } else {
        sessionStorage.removeItem(key);
      }
    } catch {
      /* ignore */
    }
  }

  static restoreListingStateIfNeeded() {
    const scope = FacetFiltersForm.getPageListingScope();
    if (!FacetFiltersForm.isSearchListingTemplate(scope)) return;

    const current = FacetFiltersForm.buildPersistentListingParams(window.location.search).toString();
    if (current) {
      FacetFiltersForm.persistListingStateFromSearchParams(window.location.search);
      return;
    }

    const key = FacetFiltersForm.getListingStateStorageKey();
    let saved = '';
    try {
      saved = sessionStorage.getItem(key) || '';
    } catch {
      return;
    }
    if (!saved) return;

    let shouldRestore = false;
    try {
      if (document.referrer) {
        const ref = new URL(document.referrer, window.location.origin);
        shouldRestore = ref.origin === window.location.origin && ref.pathname !== window.location.pathname;
      }
    } catch {
      shouldRestore = false;
    }
    if (!shouldRestore) return;

    window.location.replace(`${window.location.pathname}?${saved}`);
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    FacetFiltersForm.persistListingStateFromSearchParams(searchParams || '');
  }

  static getSections() {
    const scope = FacetFiltersForm.activeScope;
    if (scope?.dataset?.productGridId) {
      const grid = document.getElementById(scope.dataset.productGridId);
      const containerId = scope.dataset.productGridContainerId;
      if (grid?.dataset?.id) {
        return [{ section: grid.dataset.id, containerId }];
      }
    }
    const pg = document.getElementById('product-grid');
    if (!pg?.dataset?.id) return [];
    return [{ section: pg.dataset.id, containerId: 'ProductGridContainer' }];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  /** Merge facet forms so duplicate keys (e.g. sort_by) last-write-wins; sort forms merged after filter forms. */
  static mergeFacetFormSearchParams(isMobile, facetRoot, triggerForm) {
    const scope = facetRoot ? FacetFiltersForm.findFacetScope(facetRoot) : null;
    const suffix = scope?.dataset?.facetFormSuffix || '';
    const fid = (base) => (suffix ? `${base}--${suffix}` : base);

    const merged = new URLSearchParams();
    const addForm = (form) => {
      if (!form) return;
      const params = new URLSearchParams(new FormData(form));
      params.forEach((value, key) => merged.set(key, value));
    };

    const sortFormBases = ['FacetSortForm', 'FacetSortDrawerForm', 'FacetSortFormHub', 'FacetSortFormScene', 'FacetSortFormSearch'];
    const sortFormIds = new Set(sortFormBases.map((base) => fid(base)));
    const triggerId = triggerForm?.getAttribute('id') || '';
    const isSortOnlySubmit = sortFormIds.has(triggerId);

    const preserveChildCategory = () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const childCategory = urlParams.get('childCategory');
        if (childCategory) merged.set('childCategory', childCategory);
        urlParams.forEach((value, key) => {
          if (key.startsWith('filter.') && key.toLowerCase().includes('childcategory')) {
            if (!merged.getAll(key).includes(value)) {
              merged.append(key, value);
            }
          }
        });
      } catch {
        /* ignore */
      }
    };

    /** 並び替えのみの送信時: カスタム sort フォームに filter が無いので URL の絞り込みを引き継ぐ */
    if (isSortOnlySubmit) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.forEach((value, key) => {
        if (key.startsWith('filter.')) {
          merged.append(key, value);
        }
      });
      ['category', 'collection', 'scene', 'per_page', 'childCategory', 'q'].forEach((key) => {
        if (urlParams.has(key)) {
          merged.set(key, urlParams.get(key));
        }
      });
      if (urlParams.has('options[prefix]')) {
        merged.set('options[prefix]', urlParams.get('options[prefix]'));
      }
    }

    const addSortForms = () => {
      sortFormBases.forEach((base) => {
        addForm(document.getElementById(fid(base)));
      });
    };

    /** 操作元フォームの sort_by を最優先（SP ドロワー変更時に非表示 PC 用 sort フォームで上書きされない） */
    const applyTriggerSort = () => {
      if (!triggerForm?.querySelector('[name="sort_by"]')) return;
      addForm(triggerForm);
    };

    if (isMobile) {
      addForm(document.getElementById(fid('FacetFiltersFormMobile')));
      addSortForms();
      applyTriggerSort();
      merged.delete('page');
      preserveChildCategory();
      return merged.toString();
    }

    addForm(document.getElementById(fid('FacetFiltersForm')));
    addForm(document.getElementById(fid('FacetFiltersPillsForm')));
    addSortForms();
    applyTriggerSort();
    merged.delete('page');
    preserveChildCategory();
    return merged.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const trigger = event.target;
    if (trigger?.classList?.contains('mobile-facets__checkbox')) {
      const form = FacetFiltersForm.resolveFacetSubmitForm(trigger);
      if (!form) return;
      const searchParams = this.createSearchParams(form);
      this.onSubmitForm(searchParams, event);
      return;
    }

    const form = FacetFiltersForm.resolveFacetSubmitForm(trigger);
    const formId = form?.getAttribute('id') || '';
    const isMobile = formId === 'FacetFiltersFormMobile' || formId.startsWith('FacetFiltersFormMobile--');
    const searchParams = FacetFiltersForm.mergeFacetFormSearchParams(isMobile, this, form);
    this.onSubmitForm(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    const searchParams = FacetFiltersForm.mergeListingTabParamsFromCurrentUrl(url);
    FacetFiltersForm.renderPage(searchParams, event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.activeScope = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.PAGE_LISTING_SORT_KEY = 'pageProductListingSortBy';
FacetFiltersForm.PAGE_LISTING_STATE_KEY_PREFIX = 'pageProductListingState';
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
document.addEventListener('DOMContentLoaded', () => {
  FacetFiltersForm.initPageListingLinkPatchListeners();
  FacetFiltersForm.initFilterSortBarScrollClose();
  FacetFiltersForm.initFilterSortBarOutsideClose();
  FacetFiltersForm.initFilterSortBarPortalListeners();
  FacetFiltersForm.restoreListingStateIfNeeded();
  FacetFiltersForm.syncMobileFacetSortText();
  FacetFiltersForm.syncPageListingGridIfNeeded();
  FacetFiltersForm.patchPageListingNavigationLinks();
  FacetFiltersForm.persistListingStateFromSearchParams(window.location.search);
  FacetFiltersForm.rebindFilterSortBarSummaries();
});
document.addEventListener('change', (event) => {
  const t = event.target;
  if (t && t.nodeName === 'SELECT' && t.getAttribute('name') === 'sort_by') {
    try {
      sessionStorage.setItem(FacetFiltersForm.PAGE_LISTING_SORT_KEY, t.value);
    } catch {
      /* ignore */
    }
  }
});

window.addEventListener('pageshow', () => {
  FacetFiltersForm.restoreListingStateIfNeeded();
  FacetFiltersForm.patchPageListingNavigationLinks();
  FacetFiltersForm.syncMobileFacetSortText();
  FacetFiltersForm.persistListingStateFromSearchParams(window.location.search);
});

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
